const internals = {};

internals.omitBustConfig = {
    value: null,
    title: "Omit Bust",
    type: "checkbox",
    required: false,
    reducer: "flags.omitBust"
};

internals.croquisMeasurements = {
    general: {
        title: "General",
        fields: {
            title: {
                value: "",
                title: "Body Model Name",
                type: "text",
                required: true
            },
            units: {
                value: "",
                defaultValue: "in",
                title: "Unit of Measurement",
                type: "radiobutton",
                buttons: {
                    inches: {
                        value: "in",
                        label: "Inches"
                    },
                    centimeters: {
                        value: "cm",
                        label: "Centimeters"
                    }
                }
            },
            color: {
                value: "",
                title: "Line Color",
                type: "select",
                instructions:
                    'Choose the outline color for your body model. Line darkness can vary greatly from printer to printer! If you’re printing to sketch on paper, <a href="../src/static/MyBodyModel-Color-Swatch.pdf" target="_blank">print a test swatch</a> to determine the the best line color. Choose the lightest possible lines so that they will seem to disappear under your sketches. Choose darker lines if you want the lines to be more visible.',
                photo: require("images/measurements/lineColor.jpg"),
                reducer: "flags.lineColor",
                options: {
                    black: "Black",
                    darkGray: "Dark Gray",
                    lightGray: "Light Gray",
                    orange: "Orange",
                    teal: "Teal"
                },
                required: true
            },
            height: {
                value: null,
                title: "Height",
                type: "number",
                instructions:
                    'Enter your full height in inches or centimeters. E.g. If your height is 5’4", write 64. If your height is 1.55 m, write 155.',
                required: true
            }
        }
    },
    head: {
        title: "Head",
        fields: {
            neckCircumference: {
                value: null,
                title: "Neck Circumference",
                instructions:
                    "Measure around the middle of the neck at the narrowest point.",
                photo: require("images/measurements/neckCircumference.jpg"),
                type: "number",
                required: true
            },
            neckLength: {
                value: null,
                title: "Neck Length",
                instructions:
                    "Keeping the measuring tape taut (not curved) measure from your center collarbone to where your neck meets your underchin.",
                photo: require("images/measurements/neckLength.jpg"),
                type: "number",
                required: true
            }
        }
    },
    arms: {
        title: "Arms and Shoulders",
        fields: {
            shoulderWidth: {
                value: null,
                title: "Shoulder Width (Front)",
                instructions:
                    "Keeping the measuring tape taut (not curved) measure from one shoulder point to the other (shoulder seamline for tops with set-in sleeves).  These points are marked by the bones at the upper tips of your shoulders, where the shoulder meets the arm. ",
                photo: require("images/measurements/shoulderWidth.jpg"),
                type: "number",
                required: true
            },
            bicepCircumference: {
                value: null,
                title: "Bicep Circumference",
                instructions:
                    "With your arm relaxed, measure around the widest section of your upper arm.",
                photo: require("images/measurements/bicepCircumference.jpg"),
                type: "number",
                required: true
            },
            forearmCircumference: {
                value: null,
                title: "Forearm Circumference",
                instructions:
                    "Measure around your arm at the widest part of your forearm, below the elbow.",
                photo: require("images/measurements/forearmCircumference.jpg"),
                type: "number",
                required: true
            },
            armLength: {
                value: null,
                title: "Shoulder to Wrist Length",
                instructions:
                    "Measure from the tip of your shoulder cap to your wrist bone.",
                photo: require("images/measurements/armLength.jpg"),
                type: "number",
                required: true
            },
            elbowCircumference: {
                value: null,
                title: "Elbow Circumference",
                instructions:
                    "Measure around your arm at the elbow, with your arm extended.",
                photo: require("images/measurements/elbowCircumference.jpg"),
                type: "number",
                required: false
            },
            wristCircumference: {
                value: null,
                title: "Wrist Circumference",
                instructions: "Measure around your wrist at the wrist bone.",
                photo: require("images/measurements/wristCircumference.jpg"),
                type: "number",
                required: false
            }
        }
    },
    chest: {
        title: "Chest",
        fields: {
            highBust: {
                value: null,
                title: "High Bust/Chest Circumference",
                instructions:
                    "Measure the circumference of your upper chest by placing the tape around your body, just under the armpits.",
                photo: require("images/measurements/highBust.jpg"),
                type: "number",
                required: true
            },
            fullBust: {
                value: null,
                title: "Full Bust Circumference",
                instructions:
                    "Place the tape around the fullest part of the bust, around your body with the tape measure straight across the back.",
                photo: require("images/measurements/fullBust.jpg"),
                type: "number",
                required: true,
                disableIf: {
                    disablingField: "omitBust",
                    conditionalVal: true
                }
            },
            omitBust: internals.omitBustConfig,
            underBust: {
                value: null,
                title: "Under Bust/Chest Circumference",
                instructions: omitBust => {
                    if (omitBust) {
                        return {
                            instructions: `<ul>
                                <li>
                                    For a straight chest outline: Enter a number slightly lower than your High Chest Circumference.
                                    Try subtracting .5 cm or .25 inches at a time until you get the most accurate result.
                                </li>
                                <li>
                                    For a slightly curved chest outline: Enter a number slightly higher than your High Chest Circumference.
                                    Try adding .5 cm or .25 inches at a time until you get the most accurate result.
                                </li>
                            </ul>`,
                            photo: require("images/measurements/omitBustUnder.png")
                        };
                    }

                    return {
                        instructions:
                            "Measure the circumference of your lower chest by placing the tape around your body, just under the bust or chest.",
                        photo: require("images/measurements/underBust.jpg")
                    };
                },
                type: "number",
                required: true
            }
        }
    },
    waist: {
        title: "Waist",
        fields: {
            waistCircumference: {
                value: null,
                title: "Waist Circumference",
                instructions:
                    "Measure around your body along the elastic that you tied around your natural waist - i.e. where your body bends to the side.",
                photo: require("images/measurements/waistCircumference.jpg"),
                type: "number",
                required: true
            },
            waistLengthBack: {
                value: null,
                title: "Neck to Waist (Back)",
                instructions:
                    "Bend your head forward to find the most prominent bone in the back of your neck and measure vertically down the spine to the elastic along your back waist.",
                photo: require("images/measurements/waistLengthBack.jpg"),
                type: "number",
                required: true
            },
            waistLengthFront: {
                value: null,
                title: "Neck to Waist (Front)",
                instructions:
                    "Standing straight with legs together, measure along the front midline of your body, between your front collarbone and your waist elastic, keeping the tape as flat to your body as possible.",
                photo: require("images/measurements/waistLengthFront.jpg"),
                type: "number",
                required: true
            },
            waistToHip: {
                value: null,
                title: "Waist To Hip Bone",
                instructions:
                    "Standing straight with legs together, measure along the side of your body, between your waist elastic and the tip of your hip bone",
                photo: require("images/measurements/waistToHip.jpg"),
                type: "number",
                required: true
            }
        }
    },
    hip: {
        title: "Hip",
        fields: {
            highHipCircumference: {
                value: null,
                title: "High Hip Circumference",
                instructions:
                    "Measure around your body across the top of your hip bones and belly - usually about 3 inches down from your waist.",
                photo: require("images/measurements/highHipCircumference.jpg"),
                type: "number",
                required: true
            },
            fullHipCircumference: {
                value: null,
                title: "Low Hip Circumference",
                instructions:
                    "With legs together, measure around your body across the dots that you marked on your low hips, keeping the tape the same distance from the waist all the way around.",
                photo: require("images/measurements/lowHipCircumference.jpg"),
                type: "number",
                required: true
            }
        }
    },
    legs: {
        title: "Legs",
        fields: {
            thighCircumference: {
                value: null,
                title: "Thigh Circumference",
                instructions:
                    "Measure around your upper thigh, at the widest part of your thigh.",
                photo: require("images/measurements/thighCircumference.jpg"),
                type: "number",
                required: true
            },
            calfCircumference: {
                value: null,
                title: "Calf Circumference",
                instructions: "Measure around the widest part of your calf.",
                photo: require("images/measurements/calfCircumference.jpg"),
                type: "number",
                required: true
            },
            inseam: {
                value: null,
                title: "Inseam",
                instructions:
                    "Measure along the inside of your leg, from your crotch to just below your ankle bone.",
                photo: require("images/measurements/inseam.jpg"),
                type: "number",
                required: true
            },
            outseam: {
                value: null,
                title: "Outseam",
                instructions:
                    "Measure along the outside of your leg, from your waist elastic to just below your ankle bone.",
                photo: require("images/measurements/outseam.jpg"),
                type: "number",
                required: true
            },
            ankleCircumference: {
                value: null,
                title: "Ankle Circumference",
                instructions: "Measure around your ankle at the ankle bone.",
                photo: require("images/measurements/ankleCircumference.jpg"),
                type: "number",
                required: false
            }
        }
    },
    optionalAdjustments: {
        title: "Optional Adjustments",
        intro: `The following adjustments are optional. If you’re not sure how to answer these questions,
            save your model to view the preview image. If your body model doesn’t look right, click “Edit” to
            return to these questions and adjust as needed. <em>Please note: All bodies are different and there’s no one
            right way to be. The illustrations below are provided as examples only.</em>`,
        fields: {
            bustlineTweakVertical: {
                value: null,
                title: "Bustline Height",
                instructions: `Use this field to move your bustline height upward (+) or downward (-). If no adjustment is needed,
                    select "No adjustment." Not sure? Save your model to view the preview image, then click "Edit" to adjust as needed.`,
                photo: require("images/measurements/bustLineHeight.png"),
                // Note that this configuration can be reused for a Slider component essentially as-is,
                // requiring only a different get() handler in our Strangeforms config
                type: "range",
                range: {
                    min: -5,
                    max: 5,
                    step: 1,
                    zeroText: "No Adjustment"
                },
                required: false,
                disableIf: {
                    disablingField: "omitBust",
                    conditionalVal: true
                }
            },
            bustSpan: {
                title: "Bust Span",
                instructions: {
                    dialogTitle:
                        "How to move the breasts closer together or further apart",
                    text: `<p>
                        <ol>
                            <li>First, click "Create My Body Model" below to view your preview image.</li>
                            <li>
                                If adjustment is needed, click "Edit." Then, adjust your bust span manually
                                by editing your High Bust Circumference in Section 4. Chest. Try adjusting
                                by just 1-2 cm/in at a time until you get the best result.
                                <ul>
                                    <li>To move the breasts closer together  --> Decrease the High Bust Circumference</li>
                                    <li>To move the breasts further apart  -->  Increase the High Bust Circumference</li>
                                </ul>
                            </li>
                        </ol>
                    </p>
                    `
                },
                photo: require("images/measurements/bustSpan.png"),
                type: "noop",
                disableIf: {
                    disablingField: "omitBust",
                    conditionalVal: true
                }
            },
            hipCurve: {
                value: null,
                title: "Hip Curve - Visual Width (front)",
                instructions: `Looking at your body in the mirror from the front, compare the visual width of your
                    <strong>high hips</strong> and your <strong>low hips</strong>. Imagine that you’re drawing a horizontal line directly across each area
                    to determine if one is visually wider than the other. <br> <strong>Note:</strong> If you prefer to adjust your hip curve manually, select "No Adjustment"
                    here and edit your numbers directly in Section 6. Hip.`,
                photo: require("images/measurements/hipCurve.jpg"),
                type: "select",
                options: {
                    clear: "No Adjustment",
                    wider: "A.) My high hips are wider than my low hips.",
                    same: "B.) My high hips and low hips are the same width",
                    narrower: "C.) My high hips are narrower than my low hips"
                },
                required: false
            },
            thighCurveQuestionnaire: {
                value: null,
                title: "Thigh Curve - Visual Width (front)",
                // Hack to allow coloring the first word of this input's label
                // Id is set on input element, then used to select element's label for styling
                // See components/BodyModelForm/styles.scss and Select element config in components/BodyModelForm/index.js
                id: "thighCurveInput",
                // Used to selectively adjust the label style to prevent longer label
                // from overflowing onto input
                longLabel: true,
                instructions: `Looking at your body in the mirror from the front, compare the visual width of your <strong>upper thighs</strong> and
                <strong>low hips</strong>.  Select one of these options IF they describe your shape:
                <ul>
                    <li>A.) My upper thighs are wider than my low hips</li>
                    <li>B.) My upper thighs are narrower than my low hips</li>
                    <li>Note: Select "No Adjustment" if neither of these options is correct, or if you prefer to adjust your thigh curve manually within the measurement fields.</li>
                    <li style="color: #E07056">We are beta testing this new adjustment option. Please send feedback to <a href="mailto:help@mybodymodel.com"><strong>help@mybodymodel.com</strong></a>.</li>
                </ul>
                `,
                photo: require("images/measurements/thighCurve.jpg"),
                type: "select",
                // Option keys — values actually sent back to API — are conventions from the builder. See Trip's schema
                options: {
                    clear: "No Adjustment",
                    round: "A.) My upper thighs are wider than my low hips.",
                    inner: "B.) My upper thighs are narrower than my low hips."
                },
                required: false
            }
        }
    }
};

internals.getField = field => {
    let fieldObj = {};
    Object.keys(internals.croquisMeasurements).map(key => {
        Object.keys(internals.croquisMeasurements[key].fields).map(fieldKey => {
            if (field === fieldKey) {
                fieldObj = internals.croquisMeasurements[key].fields[fieldKey];
            }
        });
    });
    return fieldObj;
};

internals.isValidMeasurement = (fieldObj, value, type) => {
    if (!fieldObj.required && internals.isEmpty(value)) {
        return true;
    }

    const measurement = parseFloat(value);

    switch (type) {
        case "radiobutton":
            return value && typeof value === "string";
            break;

        case "checkbox":
            return typeof value === "boolean";
            break;

        case "select":
            return Object.keys(fieldObj.options).includes(value);
            break;

        case "range":
            return (
                !isNaN(measurement) &&
                isFinite(measurement) &&
                value >= fieldObj.range.min &&
                value <= fieldObj.range.max
            );
            break;

        default:
            return !isNaN(measurement) && isFinite(measurement);
            break;
    }
};

internals.isEmpty = val => [null, "", 0].includes(val);

module.exports = {
    fields: internals.croquisMeasurements,
    getField: internals.getField,
    isValidMeasurement: internals.isValidMeasurement
};



// WEBPACK FOOTER //
// ./src/utils/croquis-measurements.js